import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import DF from 'DisplayFramework';
import { toCurrency } from 'utils/utils';
import ParListSelect from 'components/ParListSelect';
import TasksListPopover from 'pages/workflow/TasksListPopover';
import { DataGridColDef, DataGridProps } from 'components/DataGrid/DataGrid';
import DataGrid from 'components/DataGrid';
import {
  findSIByAnyRefOrStatusOrDate,
  FindSIByAnyRefOrStatusOrDateParams,
  SIListResponse,
} from 'services/supplierInvoice';
import NewButton from 'components/NewButton';
import SIStatusTag from './SIStatusTag';
import RouterLink from 'components/RouterLink';
import moment from 'moment';
import { DatePicker, Button, Typography } from 'antd';
import { dateToString } from 'utils/utils2';
import GetLabel from 'DisplayFramework/components/GetLabel';
import IActions from 'icons/IActions';
import ThirdPartyInput from 'components/ThirdPartyInput';
import ModalControllerNV from 'components/ModalControllerNV';
import SIDetail from './SIDetail';

export type CIListSearchProps = Pick<DataGridProps<any>, 'rowSelection'> & {
  onSelect?: (item: SIListResponse) => void;
  hideCIListModal?: () => void;
  hideCol?: boolean;
  hideExtraAction?: boolean;
  ctref?: string;
  assetProd?: boolean;
};

const SIList = (props: CIListSearchProps) => {
  const { rowSelection, onSelect, ctref } = props;
  const initialSearchParams: FindSIByAnyRefOrStatusOrDateParams = {
    clientname: '',
    ctrreference: ctref ?? '',
    datemax: '',
    datemin: '',
    deliveryreference: '',
    mgid: parseInt(localStorage.getItem('mgid') ?? ''),
    orderreference: '',
    paystatus: '',
    sidocreference: '',
    sireference: '',
    sistatus: '',
    sistatus1: '',
    sistatus2: '',
    suppliername: '',
    tprefclient: '',
    tprefsupplier: '',
  };

  const [searchParams, setSearchParams] =
    useState<FindSIByAnyRefOrStatusOrDateParams>(initialSearchParams);

  const [searchs, setsearchs] = useState(props.assetProd);

  const {
    data: supplierInvoice,
    isFetching,
    refetch,
  } = useQuery(
    ['findSIByAnyRefOrStatusOrDate', { ...searchParams, ctrreference: ctref }],
    () =>
      findSIByAnyRefOrStatusOrDate({
        ...searchParams,
        ctrreference: ctref ?? searchParams.ctrreference,
      }).then((res) => {
        //  setsearchs(false);
        return res.list || [];
      }),
    { refetchOnMount: false, enabled: searchs }
  );

  const columns = useMemo<DataGridColDef<SIListResponse>[]>(
    () => [
      {
        filtrable: true,
        dataIndex: 'sireference',
        fixed: 'left',
        width: '8%',
        render: (cireference, record) =>
          onSelect ? (
            <Typography.Link onClick={() => onSelect && onSelect(record)}>
              {cireference}
            </Typography.Link>
          ) : props.assetProd ? (
            <ModalControllerNV
              content={({ setVisible, visible }) => (
                <a
                  onClick={() => {
                    setVisible(true);
                  }}
                >
                  {cireference}
                </a>
              )}
              modal={({ setVisible, visible }) => (
                <SIDetail
                  showButtons
                  showAsModal
                  visible={visible}
                  siid={record.siid}
                  onCancel={() => setVisible(false)}
                 
                />
              )}
            ></ModalControllerNV>
          ) : (
            <RouterLink to={`/supplier-invoice/${record.siid}`}>
              {cireference}
            </RouterLink>
          ),
      },
      {
        dataIndex: 'sitypelabel',
        filtrable: 'clientname',
        filterComponent: <ThirdPartyInput role="CLIENT" onlytpid />,
        width: '10%',
      },

      {
        dataIndex: 'sidocdate',
        fixed: 'left',
        filterMultiple: true,
        filtrable: 'datemaxdatemin',
        filterComponent: (
          <React.Fragment>
            <DatePicker name="datemin" placeholder="date min" />
            <DatePicker name="datemax" placeholder="date max" />
          </React.Fragment>
        ),
        width: '10%',
        render: (e) => moment(e).format('YYYY-MM-DD'),
      },
      {
        dataIndex: 'refinvoicecreditnote',
        fixed: 'left',
        filtrable: 'ctrreference',
        width: '8%',
      },
      { filtrable: true, dataIndex: 'sidocreference', width: '8%' },
      { filtrable: true, dataIndex: 'tprefsupplier', width: '8%' },
      { filtrable: true, dataIndex: 'suppliername', width: '7%' },

      {
        dataIndex: 'sigrosstotal',
        filtrable: 'deliveryreference',
        align: 'right',
        render: (val, record) => toCurrency(val, record.currcode),
        width: '8%',
      },
      {
        dataIndex: 'sidueamount',
        filtrable: 'paystatus',
        filterComponent: <ParListSelect parcode="PAYMSTATUS" />,
        align: 'right',
        render: (val, record) => toCurrency(val, record.currcode),
        width: '8%',
      },

      { dataIndex: 'orderreference', filtrable: true, width: '8%' },
      { dataIndex: 'deliveryreference', width: '8%' },
      {
        comcode: 'sistatuslabel',
        dataIndex: 'sistatus',
        filtrable: true,
        filterComponent: <ParListSelect parcode="SISTATUS" />,

        render: (sistatus, record) => (
          <SIStatusTag
            status={sistatus}
            statuslabel={record.sistatuslabel}
            key={sistatus}
          />
        ),
        width: '10%',
      },
      {
        comcode: 'actions',
        key: 'actions',
        fixed: 'right',
        width: 100,
        render: (value, record) => (
          <TasksListPopover
            entityId={Number(record.siid)}
            entityReference={record.sireference}
            entityName="SINVOICE"
            title={record.sireference + ' - ' + record.suppliername}
            onClose={refetch}
          >
            <Button size="small" icon={<IActions height="15px" width="15px" />}>
              <GetLabel label="actions" />
            </Button>
          </TasksListPopover>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, refetch]
  );

  return (
    <DF.Provider pagcode="silist">
      <DataGrid
        columns={
          !props.hideCol
            ? columns
            : columns.filter((ea) => ea.key !== 'actions')
        }
        hideExtraAction={props.hideExtraAction}
        titleComcode="supplierlist"
        dataSource={supplierInvoice}
        initialSearchValues={initialSearchParams}
        componentName="supplierInvoice_list"
        onSearch={(value) => {
          setSearchParams({
            ...value,
            datemin: dateToString(value.datemin),
            datemax: dateToString(value.datemax),
            ctrreference: ctref ?? value.ctrreference,
          });
          setsearchs(true);
        }}
        hideFilters={props.assetProd}
        isLoading={isFetching}
        rowKey={(record) => record.siid}
        rowSelection={rowSelection}
        pagination={{ pageSize: 50 }}
        scroll={{ x: 1000, y: 430 }}
        extraActions={
          !props.assetProd && (
            <NewButton comcode="header.btnnew" to="/supplier-invoice/create" />
          )
        }
      />
    </DF.Provider>
  );
};

export default SIList;
